<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <div
            v-if="compositionRootScopeEnabled"
            class="product-overview-scope-bar"
        >
            <div class="product-overview-scope-label">
                {{ $t('Page(ProductOverview):Component(Filter):composition-root-scope') }}
            </div>
            <v-select
                ref="compositionRootProductsFilter"
                v-model="selectedCompositionRootProducts"
                multiple
                class="product-overview-scope-select"
                label="displayName"
                :filterable="false"
                :options="compositionRootProductOptions"
                :loading="compositionRootProductsLoading"
                :placeholder="$t('Page(ProductOverview):Component(Filter):composition-root-scope-placeholder')"
                :append-to-body="true"
                @input="onCompositionRootProductsChange"
                @search="onCompositionRootProductsSearch"
            >
                <template #option="option">
                    <div class="product-overview-scope-option">
                        <div>{{ option.name }}</div>
                        <small v-if="option.identifier">{{ option.identifier }}</small>
                    </div>
                </template>
                <template #selected-option="option">
                    <span>{{ option.displayName }}</span>
                </template>
                <template #no-options="{ search, searching }">
                    <span v-if="searching && search">
                        {{ $t('Page(ProductOverview):Component(Filter):composition-root-scope-empty') }}
                    </span>
                    <span v-else>
                        {{ $t('Page(ProductOverview):Component(Filter):composition-root-scope-type-to-search') }}
                    </span>
                </template>
            </v-select>
        </div>
        <NestedTable
            :fields="fields"
            :active-fields="activeFields"
            :children="items"
            :name-renderer="getName"
            :search="true"
            :is-filter-active="filterActive"
            :name-title="$t('Page(ProductOverview):Component(Grid):hover-column-name')"
            @click="onRowClick"
        >

            <template #filter-name>
                <FilterInput
                    ref="nameFilter"
                    @search="onNameSearch"
                    :initial-value="getInitialFilterValue('name')"
                    :placeholder="$t('Component(Grid):column-name')"/>
            </template>

            <template #filter-pid>
                <FilterInput
                    ref="productIdFilter"
                    @search="onProductIdSearch"
                    :initial-value="getInitialFilterValue('productId')"
                    :placeholder="$t('Component(Grid):column-id')" />
            </template>

            <template #filter-ean>
                <FilterInput
                    ref="eanFilter"
                    @search="onEanSearch"
                    :initial-value="getInitialFilterValue('ean')"
                    :placeholder="$t('Component(Grid):column-ean')" />
            </template>

            <template #filter-partNumber>
                <FilterInput
                    ref="partNumberFilter"
                    @search="onPartNumberSearch"
                    :initial-value="getInitialFilterValue('partNumber')"
                    :placeholder="$t('Component(Grid):column-part-number')" />
            </template>

            <template #filter-supplierId>
                <FilterInput
                    ref="supplierIdFilter"
                    @search="onSupplierIdSearch"
                    :initial-value="getInitialFilterValue('supplierProductId')"
                    :placeholder="$t('Component(Grid):column-supplier-id')" />
            </template>

            <template #filter-category>
                <FilterInput
                    ref="productCategoryFilter"
                    @search="onCategorySearch"
                    :initial-value="getInitialFilterValue('productCategoryFilter')"
                    :placeholder="$t('Component(Grid):column-category')" />
            </template>

            <template #filter-businessRoles>
                <BusinessRoleFilter
                    ref="businessRoles"
                    :initial-value="getInitialFilterValue('businessRoles')"
                    @search="onBusinessRoleSearch"
                />
            </template>

            <template #filter-organization>
                <AjaxVueSelectFilter
                    ref="organizationFilter"
                    :url="allOrganizationsEndpoint"
                    :cache="false"
                    cache-id="product-overview-organization-filter"
                    :initial-value="getInitialFilterValue('organizations')"
                    :placeholder="$t('Component(Grid):column-organization-unit')"
                    @search="onOrganizationSearch"
                />
            </template>

            <template #filter-supplier>
<!--                this div is important as it prevents wrong filter data - legislation statuses displaying here on column visibility toggle -->
                <div>
                    <AjaxVueSelectFilter
                        ref="supplierFilter"
                        :url="allSuppliersEndpoint"
                        :cache="true"
                        cache-id="product-overview-supplier-filter"
                        :initial-value="getInitialFilterValue('suppliers')"
                        :placeholder="$t('Component(Grid):column-supplier')"
                        @search="onSupplierSearch"
                    />
                </div>
            </template>

            <template v-for="field in fields.filter(f => f.legislationStatusFilter)" #[field.legislationStatusFilter]>
                <AjaxVueSelectFilter
                    ref="legislationStatusesFilter"
                    :cache="true"
                    :optionsNormalizer="normalizeLegislationStatuses"
                    :cache-id="'product-overview-legislation-status' + field.legislation.id"
                    :initial-value="getInitialFilterValue('legislation', field)"
                    :url="field.endpoint"
                    @search="(s) => onLegislationStatusChange(field.legislation, s)"
                />
            </template>

            <template #cell-businessRoles="data">
                <div>
                    <div v-for="br in data.item.businessRoles">{{ br.title }}</div>
                </div>
            </template>

            <template #cell-supplier="data">
                <div>{{ data.item.supplier ? data.item.supplier.title : '' }}</div>
            </template>

            <template #cell-category="data">
                <div>
                    <div v-for="category in data.item.categories">{{ category.title }}</div>
                </div>
            </template>

            <template #cell-status="data">
                <ProductOverviewStatusDot :meta="getProductOverviewStatusMeta(data.item)" />
            </template>

            <template
                v-for="field in fields.filter(f => f.legislationStatusCell)"
                #[field.legislationStatusCell]="data"
            >
                <ComplianceStatusExplanation
                    v-if="getLegislationStatusDisplay(data.item, field.legislation).title"
                    :key="field.legislationStatusCell"
                    :product-id="data.item.id"
                    :legislation-id="field.legislation.id"
                    show-description
                >
                    <template #default="{ state, toggle }">
                        <span
                            :title="field.title"
                            class="compliance-status"
                            :class="'compliance-status-' + state"
                            @click.stop="toggle"
                        >
                            <ShimmerText :active="state === 'loading'">
                                {{ getLegislationStatusDisplay(data.item, field.legislation).title }}
                            </ShimmerText>
                        </span>
                    </template>
                </ComplianceStatusExplanation>
            </template>
        </NestedTable>
        <div class="pl-2 pr-2 pb-2 d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center justify-content-center">
                <div>{{ $t('Component(Grid):records-found', {total: totalItems}) }}</div>
                <div class="pl-1 pr-1">
                    <b-form-select
                        id="perPageSelect"
                        v-model="perPage"
                        @change="onPerPageChange"
                        inline
                        :options="pageOptions"
                    />
                </div>
                <div>{{ $t('Component(Grid):records-per-page') }}</div>
            </div>
            <div>
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalItems"
                    @change="onPageChange"
                    :per-page="perPage"
                    first-number
                    last-number
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mb-0"
                >
                    <template #prev-text>
                        <feather-icon
                            icon="ChevronLeftIcon"
                            size="18"
                        />
                    </template>
                    <template #next-text>
                        <feather-icon
                            icon="ChevronRightIcon"
                            size="18"
                        />
                    </template>
                </b-pagination>
            </div>
        </div>
    </b-overlay>
</template>
<script>
import {
    BCardText, BTable, BCardTitle, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup, BOverlay,
    BCollapse, VBToggle, BPagination, BFormSelect,
} from 'bootstrap-vue'
import {ProductAttribute, ProductCategorySelect} from "@core/components/product";
import {LegislationApi, objectToGETParams, ProductApi} from "@core/api";
// import NestedTable from "@/views/product/components/product-overview/NestedTableV2";
import {NestedTable} from "@/views/product/components/product-overview";
import Ripple from 'vue-ripple-directive'
import {FilterInput, AjaxVueSelectFilter, BusinessRoleFilter} from "@core/components/common/filter";
import {UserRoute} from "@core/api";
import {getNameOrId, getScipId, ProductIdentifiers, ProductNames, ProductType} from "@/libs/product";
import {LegislationKey, legislationTranslationMixin, LegislationProductStatus} from "@core/domain/legislation";
import ProductOverviewStatusDot from "@core/components/product/ProductOverviewStatusDot.vue";
import ComplianceStatusExplanation from "@core/components/product/ComplianceStatusExplanation.vue";
import ShimmerText from "@core/components/common/ShimmerText.vue";
import vSelect from 'vue-select'

const PRODUCT_OVERVIEW_STATUS = {
    GRAY: 'gray',
    GREEN: 'green',
    RED: 'red',
    YELLOW: 'yellow',
};

const PRODUCT_OVERVIEW_GREEN_STATUSES = new Set([
    LegislationProductStatus.NOT_APPLICABLE,
    LegislationProductStatus.DOES_NOT_INCLUDE_CONCERN_ELEMENTS,
    LegislationProductStatus.INCLUDES_CONCERN_ELEMENTS_INVESTIGATED,
    LegislationProductStatus.SCIP_NOTIFICATION_SUCCEEDED,
    LegislationProductStatus.SCIP_REFERENCE,
    LegislationProductStatus.COMPLIANT,
    LegislationProductStatus.EXEMPTED,
]);

export default {
    name: 'ProductOverview',
    mixins: [legislationTranslationMixin],
    components: {
        BCard, BCardText, BButton, BOverlay, ProductAttribute, ProductCategorySelect, BRow, BCol, BFormGroup,
        BCardGroup, BTable, BCardTitle, NestedTable, FilterInput, AjaxVueSelectFilter,
        BCollapse, BPagination, BFormSelect, BusinessRoleFilter, ProductOverviewStatusDot,
        ComplianceStatusExplanation, ShimmerText, vSelect
    },
    directives: {
        Ripple,
        'b-toggle': VBToggle,
    },
    props: {
        activeFields: {
            type: Array,
            default() {
                return [];
            }
        },
        children: {
            type: Array,
            default() {
                return [];
            }
        },
        nested: {
            type: Boolean,
            default: false
        },
        compositionRootScopeEnabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showLoading: false,

            inited: false,
            currentPage: 1,
            perPage: 10,
            initialPage: 1,
            pageOptions: [5, 10, 15, 30],

            showExpandColumn: false,
            fields: [
                {
                    key: '_id_',
                    label: '_ID_',
                    order: 1,
                    renderer: (p) => p.id,
                },
                {
                    key: 'pid',
                    label: this.$t('Component(Grid):column-id'),
                    title: this.$t('Page(ProductOverview):Component(Grid):hover-column-id'),
                    order: 2,
                    active: true,
                    renderer: this.getPid,
                    search: true
                },
                {
                    key: 'ean',
                    label: this.$t('Component(Grid):column-ean'),
                    title: this.$t('Component(Grid):hover-column-ean'),
                    order: 3,
                    active: true,
                    renderer: this.getEAN,
                    search: true
                },
                {
                    key: 'partNumber',
                    label: this.$t('Component(Grid):column-part-number'),
                    title: this.$t('Component(Grid):hover-column-part-number'),
                    order: 4,
                    active: false,
                    renderer: this.getPartNumber,
                    search: true
                },
                {
                    key: 'supplierId',
                    label: this.$t('Component(Grid):column-supplier-id'),
                    title: this.$t('Component(Grid):hover-column-supplier-id'),
                    order: 5,
                    active: true,
                    renderer: this.getSupplierId,
                    search: true
                },
                {
                    key: 'category',
                    label: this.$t('Component(Grid):column-category'),
                    order: 6,
                    active: false,
                    search: true
                },
                {
                    key: 'organization',
                    label: this.$t('Component(Grid):column-organization-unit'),
                    order: 7,
                    active: false,
                    renderer: this.getOrganization,
                    search: true
                },
                {
                    key: 'businessRoles',
                    label: this.$t('Component(Grid):column-business-roles'),
                    active: true,
                    order: 8,
                    search: true
                },
                {
                    key: 'supplier',
                    label: this.$t('Component(Grid):column-supplier'),
                    active: true,
                    order: 9,
                    search: true
                },
                {
                    key: 'supplierName',
                    label: this.$t('Component(Grid):column-supplier-product-name'),
                    title: this.$t('Component(Grid):column-supplier-product-name'),
                    order: 5,
                    active: false,
                    renderer: this.getSupplierName,
                    search: false
                },
                {
                    key: 'status',
                    label: this.$t('Component(Grid):column-status'),
                    title: this.$t('Page(ProductOverview):Component(Grid):hover-column-status'),
                    order: 10,
                    active: true,
                    renderer: this.getProductOverviewStatusColor,
                    search: false,
                },
            ],
            filters: {
                id: {},
                compositionRootProductIds: [],
                // legislationStatuses: []
                legislationStatuses: {}
            },
            compositionRootProductOptions: [],
            compositionRootProductsLoading: false,
            compositionRootProductsSearchTimer: null,
            selectedCompositionRootProducts: [],
            items: this.children,
            totalItems: 0,
            legislations: [],
            mode: 'o',  // o or f, o means overview f - filter

            allSuppliersEndpoint: UserRoute.supplyChain.allSuppliers(),
            allOrganizationsEndpoint: UserRoute.supplyChain.organizations(),

            filterTimer: null,
            filterActive: false,
        }
    },
    computed: {
        visibleLegislationRefs() {
            return this.activeFields
                .filter(field => field.legislation && field.legislation.id)
                .map(field => ({
                    id: field.legislation.id,
                    key: field.legislation.key,
                }));
        }
    },
    methods: {
        //  todo refactor-simplify
        onLegislationStatusChange(legislation, statuses) {
            if (statuses.length === 0) {
                delete this.filters.legislationStatuses[legislation.id];
            } else {
                this.filters.legislationStatuses[legislation.id] = statuses.map(s => s.id);
            }
            this.filter();
        },
        onBusinessRoleSearch(roles) {
            this.filters.businessRoles = roles.map(r => r.id);
            this.filter();
        },
        onOrganizationSearch(organizations) {
            this.filters.organizations = organizations.map(o => o.id);
            this.filter();
        },
        onSupplierSearch(suppliers) {
            this.filters.suppliers = suppliers.map(s => s.id);
            this.filter();
        },
        onNameSearch(name) {
            this.filters.name = name;
            this.filter();
        },
        onProductIdSearch(id) {
            this.filters.id[ProductIdentifiers.PRODUCT_ID] = id;
            this.filter();
        },
        onEanSearch(ean) {
            this.filters.id[ProductIdentifiers.EAN] = ean;
            this.filter();
        },
        onPartNumberSearch(number) {
            this.filters.id[ProductIdentifiers.PART_NUMBER] = number;
            this.filter();
        },
        onSupplierIdSearch(id) {
            this.filters.id[ProductIdentifiers.SUPPLIERS_PRODUCT_ID] = id;
            this.filter();
        },
        onCategorySearch(category) {
            this.filters.category = category;
            this.filter();
        },
        normalizeCompositionRootProduct(product) {
            const identifier = product.identifier || '';
            const displayName = identifier ? product.name + ' (' + identifier + ')' : product.name;

            return {
                id: product.id,
                name: product.name,
                identifier,
                displayName,
            };
        },
        searchCompositionRootProducts(qs = '') {
            this.compositionRootProductsLoading = true;

            ProductApi.getCompositionRoots(qs, 1, 10).then(r => {
                const selectedIds = this.selectedCompositionRootProducts.map(product => product.id);
                const selectedOptions = this.selectedCompositionRootProducts.filter(product => {
                    return !r.data.items.find(item => item.id === product.id);
                });

                this.compositionRootProductOptions = r.data.items
                    .map(this.normalizeCompositionRootProduct)
                    .concat(selectedOptions)
                    .filter((product, index, products) => {
                        return products.findIndex(item => item.id === product.id) === index;
                    });

                this.selectedCompositionRootProducts = this.selectedCompositionRootProducts
                    .filter(product => selectedIds.includes(product.id));
            }).finally(() => {
                this.compositionRootProductsLoading = false;
            });
        },
        onCompositionRootProductsSearch(qs) {
            clearTimeout(this.compositionRootProductsSearchTimer);
            this.compositionRootProductsSearchTimer = setTimeout(() => {
                this.searchCompositionRootProducts(qs || '');
            }, 400);
        },
        onCompositionRootProductsChange(products) {
            this.selectedCompositionRootProducts = products;
            this.syncCompositionRootProductIds();
            this.filter();
        },
        syncCompositionRootProductIds() {
            const ids = this.compositionRootScopeEnabled
                ? this.selectedCompositionRootProducts.map(product => product.id)
                : [];

            if (this.filters.hasOwnProperty('compositionRootProductIds')) {
                this.filters.compositionRootProductIds = ids;
            } else {
                this.$set(this.filters, 'compositionRootProductIds', ids);
            }
        },
        filter() {
            clearTimeout(this.filterTimer);
            this.showLoading = true;

            if (this.isFilterActive()) {
                this.filterTimer = setTimeout(() => {
                    if (this.mode === 'o') {
                        this.currentPage = 1;
                    }

                    this.saveFilterAndPaging();

                    ProductApi.productOverviewFilter(this.filters, this.currentPage, this.perPage).then(r => {
                        this.filterActive = true;
                        this.totalItems = r.data.totalItems;
                        this.items = r.data.items;
                        this.mode = 'f';
                    }).finally(() => {
                        this.showLoading = false;
                    });
                }, 400);
            } else {
                this.filterActive = false;
                this.fetchProductOverview();
            }
        },

        saveFilterAndPaging() {
            const data = {
                page: {
                    perPage: this.perPage,
                    page: this.currentPage,
                    mode: this.mode
                },
                filter: this.filters,
                compositionRootScopeEnabled: this.compositionRootScopeEnabled,
                compositionRootProducts: this.selectedCompositionRootProducts
            };

            this.$store.dispatch('setRuntimeProperty', {
                name: 'productOverviewSettings',
                value: data,
                removeOnOrganizationSwitch: true
            })
        },

        restoreFilterAndPaging() {
            let data = this.$store.getters.runtimeConfig.productOverviewSettings;

            if (data) {
                this.perPage = data.page.perPage;
                this.initialPage = data.page.page;
                this.mode = data.page.mode;

                this.filters = data.filter;
                this.selectedCompositionRootProducts = data.compositionRootProducts || [];
                this.syncCompositionRootProductIds();
            }
        },

        getInitialFilterValue(name, params) {
            switch (name) {
                case 'name':
                    return this.filters.name;
                case 'productId':
                    return this.filters.id[ProductIdentifiers.PRODUCT_ID] || '';
                case 'ean':
                    return this.filters.id[ProductIdentifiers.EAN] || '';
                case 'partNumber':
                    return this.filters.id[ProductIdentifiers.PART_NUMBER] || '';
                case 'supplierProductId':
                    return this.filters.id[ProductIdentifiers.SUPPLIERS_PRODUCT_ID] || '';
                case 'productCategoryFilter':
                    return this.filters.category || '';
                case 'businessRoles':
                    return this.filters.businessRoles || [];
                case 'organizations':
                    return this.filters.organizations || [];
                case 'suppliers':
                    return this.filters.suppliers || [];
                case 'compositionRootProductIds':
                    return this.filters.compositionRootProductIds || [];
                case 'legislation':
                    if (this.filters.legislationStatuses && this.filters.legislationStatuses.hasOwnProperty(params.legislation.id)) {
                        return this.filters.legislationStatuses[params.legislation.id];
                    }
                    return [];
            }
        },

        clearFilter() {
            Object.keys(this.filters.legislationStatuses).forEach(k => {
                delete this.filters.legislationStatuses[k];
            });

            if (this.filters.id) {
                Object.keys(this.filters.id).forEach(k => {
                    delete this.filters.id[k];
                });
            }

            if (this.filters.businessRoles) {
                this.filters.businessRoles = [];
            }

            if (this.filters.organizations) {
                this.filters.organizations = [];
            }

            if (this.filters.suppliers) {
                this.filters.suppliers = [];
            }
            if (this.filters.compositionRootProductIds) {
                this.filters.compositionRootProductIds = [];
            }
            this.selectedCompositionRootProducts = [];

            delete this.filters['category'];
            delete this.filters['name'];
            delete this.filters['suppliers'];

            [
                this.$refs.nameFilter,
                this.$refs.eanFilter,
                this.$refs.partNumberFilter,
                this.$refs.productIdFilter,
                this.$refs.supplierIdFilter,
                this.$refs.productCategoryFilter,
                this.$refs.businessRoles,
                this.$refs.organizationFilter,
                this.$refs.supplierFilter,
            ].map(component => {
                if (component) {
                    component.clear();
                }
            })

            if (this.$refs.legislationStatusesFilter) {
                this.$refs.legislationStatusesFilter.map(c => {
                    if (c) {
                        c.clear();
                    }
                })
            }

            this.filter();
        },

        isFilterActive() {
            if (this.filters.name) {
                return true;
            }
            if (this.filters.category) {
                return true;
            }
            if (this.filters.id) {
                let active = Object.keys(this.filters.id).map(k => this.filters.id[k] !== '' && this.filters.id[k] !== null);
                if (active.includes(true)) {
                    return true;
                }
            }
            if (this.filters.suppliers && this.filters.suppliers.length > 0) {
                return true;
            }
            if (this.filters.organizations && this.filters.organizations.length > 0) {
                return true;
            }
            if (this.filters.businessRoles && this.filters.businessRoles.length > 0) {
                return true;
            }
            if (this.filters.compositionRootProductIds && this.filters.compositionRootProductIds.length > 0) {
                return true;
            }
            // if (this.filters.legislationStatuses.length > 0) {
            if (Object.keys(this.filters.legislationStatuses).length > 0) {
                return true;
            }
            return false;
        },
        fetchProductOverview() {
            this.showLoading = true;
            //  refactor this
            if (this.mode === 'f') {
                this.currentPage = 1;
            }

            this.saveFilterAndPaging();

            ProductApi.getProductsForProductOverview(this.currentPage, this.perPage).then(r => {
                r.data.items.forEach((product) => {
                    product.expanded = [];
                });
                this.totalItems = r.data.totalItems;
                this.items = r.data.items;
                this.mode = 'o';
            }).finally(() => this.showLoading = false);
        },
        onPerPageChange(perPage) {
            this.perPage = perPage;
            this.filter();
        },
        onPageChange(page) {
            this.currentPage = page;
            this.filter();
        },
        getEAN(product) {
            return this.getDynamicFieldValue(product, 'identifiers', 'EAN');
        },
        getPartNumber(product) {
            return this.getDynamicFieldValue(product, 'identifiers', 'Part number');
        },
        getSupplierId(product) {
            return this.getDynamicFieldValue(product, 'identifiers', 'Supplier’s product ID');
        },
        getSupplierName(product) {
            return this.getDynamicFieldValue(product, 'names', ProductNames.SUPPLIERS_PRODUCT_NAME);
        },
        gotoCreateProduct() {
            this.$router.push({name: 'product-new'});
        },
        getPid(product) {
            return this.getDynamicFieldValue(product, 'identifiers', 'Product ID');
        },
        getName(product) {
            return getNameOrId(product.names, ProductNames.OUR_PRODUCT_NAME) ||
                (product.names.length > 0 ? product.names[0].value : '');
        },
        getCategories(product) {
            return product.categories.map(c => c.title);
        },

        getOrganization(product) {
            return product.organization ? product.organization.title : '';
        },

        renderField(field, product) {
            if (field.renderer) {
                return field.renderer(product);
            }

            if (product.hasOwnProperty(field.key)) {
                return product[field.key];
            }

            return '';
        },

        toggleChildren(product, level) {
            let key = product.expanded.findIndex(l => l === level);

            if (key > -1) {
                product.expanded.splice(key, level);
            } else {
                product.expanded.push(level);
            }
        },

        onRowClick(product) {
            this.$emit('click', product);
        },

        getDynamicFieldValue(product, type, name) {
            let p = product[type].find((r) => {
                return r.key === name;
            });

            if (p) {
                return p.value;
            }

            return '';
        },
        isProductChildVisible(product, level, child) {
            return product.expanded.includes(level)
        },
        resolveLegislationHoverHint(legislation) {
            if (legislation.id === 1) {
                return this.$t('Page(ProductOverview):Component(Grid):hover-column-l-reach');
            } else if (legislation.id === 2) {
                return this.$t('Page(ProductOverview):Component(Grid):hover-column-l-art-33');
            }
            return null;
        },
        normalizeLegislationStatuses(statuses) {
            return statuses.map(s => {
                return {
                    id: s.id,
                    title: this.$t(this.makeProductStatusTranslationKey(s.id)),
                }
            })
        },
        getLegislationStatusItem(product, legislationRef) {
            if (!product || !product.legislationsEventStatus || !legislationRef) {
                return null;
            }

            return product.legislationsEventStatus[legislationRef.id]
                || product.legislationsEventStatus[String(legislationRef.id)]
                || product.legislationsEventStatus[legislationRef.key]
                || null;
        },
        getLegislationStatusId(statusItem) {
            if (!statusItem) {
                return '';
            }

            if (typeof statusItem.statusId === 'string' && statusItem.statusId.trim() !== '') {
                return statusItem.statusId.trim().toLowerCase();
            }

            if (!statusItem.status) {
                return '';
            }

            const status = statusItem.status;

            if (typeof status === 'string') {
                return status.trim().toLowerCase();
            }

            if (typeof status === 'object') {
                const value = status.id || status.key || status.status || '';
                return typeof value === 'string' ? value.trim().toLowerCase() : '';
            }

            return '';
        },
        getLegislationStatusTitle(statusItem) {
            if (!statusItem || !statusItem.status) {
                return '';
            }

            if (typeof statusItem.status === 'string') {
                return statusItem.status;
            }

            if (typeof statusItem.status === 'object' && statusItem.status.title) {
                return statusItem.status.title;
            }

            return '';
        },
        getLegislationStatusDisplay(product, legislation) {
            const legislationRef = {id: legislation.id, key: legislation.key};
            const statusItem = this.getLegislationStatusItem(product, legislationRef);
            const statusTitle = this.getLegislationStatusTitle(statusItem);
            const statusId = this.getLegislationStatusId(statusItem);

            let title = statusTitle;
            if (statusId && legislation.key === LegislationKey.SCIP && getScipId(product)) {
                title += ' (S)';
            }

            return {
                title,
                statusItem,
                statusId,
                isEmpty: !title,
            };
        },
        getProductOverviewStatusMeta(product) {
            if (product && [ProductType.FICTITIOUS, ProductType.GROUP].includes(product.type)) {
                return {
                    color: '',
                    titleKey: '',
                    isEmpty: true,
                };
            }

            if (this.visibleLegislationRefs.length === 0) {
                return {
                    color: '',
                    titleKey: '',
                    isEmpty: true,
                };
            }

            const statuses = this.visibleLegislationRefs
                .map((legislationRef) => this.getLegislationStatusItem(product, legislationRef))
                .map(this.getLegislationStatusId)
                .filter(Boolean);

            let color = PRODUCT_OVERVIEW_STATUS.YELLOW;

            if (statuses.length === 0 || statuses.every(s => s === LegislationProductStatus.NEW)) {
                color = PRODUCT_OVERVIEW_STATUS.GRAY;
            } else if (statuses.includes(LegislationProductStatus.NOT_COMPLIANT)) {
                color = PRODUCT_OVERVIEW_STATUS.RED;
            } else if (statuses.every(s => PRODUCT_OVERVIEW_GREEN_STATUSES.has(s))) {
                color = PRODUCT_OVERVIEW_STATUS.GREEN;
            }

            return {
                color,
                titleKey: 'Page(ProductOverview):Component(Grid):status-' + color,
                isEmpty: false,
            };
        },
        getProductOverviewStatusColor(product) {
            return this.getProductOverviewStatusMeta(product).color;
        }
    },
    created() {
        this.restoreFilterAndPaging();
        this.filter();
        LegislationApi
            .all()
            .then(r => {
                this.legislations = r.data;

                this.legislations.forEach(l => {
                    this.fields.push({
                        key: 'legislation_' + l.id,
                        label: l.shortName || l.title,
                        active: true,
                        title: this.resolveLegislationHoverHint(l),
                        order: this.fields.length + 1,
                        renderer: (product) => {
                            return this.getLegislationStatusDisplay(product, l).title;
                        },
                        legislation: l,
                        legislationStatusCell: 'cell-' + 'legislation_' + l.id,
                        legislationStatusFilter: 'filter-' + 'legislation_' + l.id,
                        endpoint: UserRoute.legislation.legislationStatuses(l.id),
                    });

                    this.$emit('fieldsChange');
                });
            })
        ;
    },
    mounted() {
        this.searchCompositionRootProducts();
    },
    watch: {
        compositionRootScopeEnabled() {
            this.syncCompositionRootProductIds();
            this.filter();
        },
    },
};
</script>
<style>
.business-roles-select .vue-treeselect__multi-value-item-container {
    display: block;
}
.table-responsive.nested-product-overview-container {
    margin: 0;
}
td.children-container {
    padding: 0;
}
.compliance-status {
    border-bottom: 1px dotted currentColor;
    cursor: pointer;
    display: inline-block;
    margin: -0.125rem -0.375rem;
    outline: none;
    padding: 0.125rem 0.375rem;
}
.compliance-status-loading {
    cursor: wait;
}
.compliance-status-error {
    color: #ea5455;
}
.product-overview-scope-bar {
    align-items: center;
    border-bottom: 1px solid #ebe9f1;
    display: flex;
    gap: 10px;
    padding: 10px;
}
.product-overview-scope-label {
    color: #6e6b7b;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}
.product-overview-scope-select {
    flex: 1 1 280px;
    min-width: 260px;
}
.product-overview-scope-option small {
    color: #6e6b7b;
}
</style>
